import cx from 'clsx';
import React, { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';

import { ReactComponent as ViewDetailsIcon } from '@assets/svg/executive-summary/ic-view-details.svg';
import { ReactComponent as TableSortIcon } from '@assets/svg/table-sort.svg';

import MultiTableSortMenu from '@components/common/MultiTableSortMenu/MultiTableSortMenu';
import TableEmptyRow from '@components/common/TableEmptyRow/TableEmptyRow';
import TooltipSort from '@components/common/TooltipSort/TooltipSort';

import { initialSorting } from '@constants/tableSorting';

import useTranslation from '@hooks/useTranslation';

import type { ITableSorting } from '@model/tableSorting';

import { selectAppLocationSelectValue } from '@store/useAppStore';

import { useEquipmentByFaultQuery } from '../../services/useEquipmentByFaultQuery';
import {
  selectFailureCauseCategoriesDateFilter,
  selectFailureCauseCategoriesStatusFilter,
} from '../../store/useFailureCauseCategoriesStore';
import EquipmentByFaultTableSkeleton from './EquipmentByFaultTableSkeleton';
import style from './style.module.scss';

interface IProps {
  faultId: number;
  aiTotal: number;
  isVisibleHiddenRow: boolean;
}

const EquipmentByFaultTable: React.FC<IProps> = ({ faultId, aiTotal, isVisibleHiddenRow }) => {
  const { t } = useTranslation();
  const [sorting, setSorting] = useState<ITableSorting>(initialSorting);

  const { customerId } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const locationSelectValue = selectAppLocationSelectValue();
  const statusFilter = selectFailureCauseCategoriesStatusFilter();
  const { dateFrom, dateTo } = selectFailureCauseCategoriesDateFilter();

  const { data, isLoading } = useEquipmentByFaultQuery({
    faultId,
    locationIds:
      locationSelectValue.level_name === 'allFacilities' ? undefined : locationSelectValue?.filterIds,
    sorting,
    enabled: isVisibleHiddenRow,
    statusType: statusFilter,
    dateFrom,
    dateTo,
  });

  const resetSorting = () => {
    setSorting(initialSorting);
  };

  const onChangeSorting = (key: string, fieldName?: string) => {
    if (!sorting.id || sorting.id !== key) {
      setSorting({ id: key, desc: false, fieldName });
      return;
    }

    const { id, desc } = sorting;

    if (desc) {
      resetSorting();
    } else {
      setSorting({ id, desc: true, fieldName });
    }
  };

  const TOOLTIP_ID = `equipment-by-fault-sort-tooltip-${faultId}`;

  const selectedAllFacilitiesOrGroups =
    locationSelectValue.id === 'all' || locationSelectValue.level_name === 'group';

  const sortingOptions = [
    { label: t('Location'), value: 'location_name' },
    { label: t('Facility'), value: 'facility_name' },
  ];

  return (
    <td className={style.equipmentByFaultTableWrapper} colSpan={3}>
      <table className={style.equipmentByFaultTable}>
        <thead>
          <tr>
            <th>
              <div className={cx({ [style.multiTableSortMenuWrapper]: selectedAllFacilitiesOrGroups })}>
                {t('Location')}
                {selectedAllFacilitiesOrGroups ? (
                  <MultiTableSortMenu
                    id={faultId}
                    onChange={onChangeSorting}
                    sorting={sorting}
                    sortingTooltipId={TOOLTIP_ID}
                    sortingOptions={sortingOptions}
                    resetSorting={resetSorting}
                  />
                ) : (
                  <TableSortIcon
                    className={cx(style.sortIcon, { [style.activeSort]: sorting.id === 'location_name' })}
                    onClick={() => onChangeSorting('location_name')}
                    data-tooltip-content="location_name"
                    data-tooltip-id={TOOLTIP_ID}
                  />
                )}
              </div>
            </th>
            <th>
              {t('Equipment')}
              <TableSortIcon
                className={cx(style.sortIcon, { [style.activeSort]: sorting.id === 'equipment_name' })}
                onClick={() => onChangeSorting('equipment_name')}
                data-tooltip-content="equipment_name"
                data-tooltip-id={TOOLTIP_ID}
              />
            </th>
            <th>
              {t('Component')}
              <TableSortIcon
                className={cx(style.sortIcon, { [style.activeSort]: sorting.id === 'component_name' })}
                onClick={() => onChangeSorting('component_name')}
                data-tooltip-content="component_name"
                data-tooltip-id={TOOLTIP_ID}
              />
            </th>
            <th>
              {t('Action Item Status')}
              <TableSortIcon
                className={cx(style.sortIcon, { [style.activeSort]: sorting.id === 'status_name' })}
                onClick={() => onChangeSorting('status_name')}
                data-tooltip-content="status_name"
                data-tooltip-id={TOOLTIP_ID}
              />
            </th>
            <th />
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <EquipmentByFaultTableSkeleton total={aiTotal} />
          ) : data?.length ? (
            data?.map((row) => (
              <tr className={style.bodyRow} key={row.action_item_id}>
                <td>
                  {row.location_name}
                  {selectedAllFacilitiesOrGroups && (
                    <div className={style.facilityName}>{row.facility_name}</div>
                  )}
                </td>
                <td>{row.equipment_name}</td>
                <td>{row.component_name}</td>
                <td>{row.status_name === 'Closed' ? 'Archived' : row.status_name}</td>
                <td
                  id="view-action-item"
                  className={style.iconColumn}
                  onClick={() =>
                    navigate(`/customer/${customerId}/ai/${row.action_item_id}`, {
                      state: {
                        prevPage: pathname,
                      },
                    })
                  }
                >
                  <ViewDetailsIcon className={style.viewDetailsIcon} />
                </td>
              </tr>
            ))
          ) : (
            <TableEmptyRow text={t('No equipment were found.')} />
          )}
        </tbody>
      </table>
      <Tooltip anchorSelect="#view-action-item" content={t('View Action Item')} />
      <TooltipSort id={TOOLTIP_ID} sorting={sorting} />
    </td>
  );
};

export default EquipmentByFaultTable;
